import React from 'react';
import './HomeRedesign.css';

const GUEST_DRAFT_STORAGE_KEY = 'editpage:guest-homepage-draft';
const EDITOR_SAVE_STATE_STORAGE_KEY = 'editpage:homepage-editor-save-state';
const SIGNUP_DISABLED = true;

const SCENES = [
  {
    match: ['bak', 'candle', 'cafe', 'coffee', 'restaurant', 'menu', 'shop', 'store', 'sell', 'product'],
    tag: '// generated from your prompt',
    brand: 'Warmly',
    headlineParts: ['A little place people ', 'come back to'],
    sub: "Show what you're serving, take orders online, and let people order ahead - all from one page you built in a minute.",
    cta: 'Order online',
    ctaTop: 'Order now',
    media: 'Product photo grid',
    features: [
      ['Menu builder', 'List items, prices, and photos without touching code.'],
      ['Online ordering', 'Customers order and pay right from the page.'],
      ['Table and pickup times', 'Let people reserve a table or a pickup slot.'],
    ],
    accent: 'var(--home-accent)',
  },
  {
    match: ['photo', 'portfolio', 'wedding', 'design', 'studio', 'creative', 'art'],
    tag: '// generated from your prompt',
    brand: 'Studio Nine',
    headlineParts: ['Work that speaks ', 'for itself'],
    sub: 'A gallery-first layout that puts your best work up top, with an inquiry form that turns visitors into bookings.',
    cta: 'View the work',
    ctaTop: 'Book a session',
    media: 'Gallery, 12 images',
    features: [
      ['Gallery layouts', 'Drop in photos and the grid arranges itself.'],
      ['Client proofing', 'Share private galleries clients can favorite from.'],
      ['Booking form', 'Collect inquiries with dates, no back-and-forth email.'],
    ],
    accent: 'var(--home-lavender)',
  },
  {
    match: ['train', 'fitness', 'gym', 'coach', 'workout', 'health'],
    tag: '// generated from your prompt',
    brand: 'Forge',
    headlineParts: ['Train with a plan, ', 'not a guess'],
    sub: 'A bold, high-contrast page built to convert - programs up front, a clear call to book your first session.',
    cta: 'Book a session',
    ctaTop: 'Get started',
    media: 'Hero action shot',
    features: [
      ['Program pages', 'Break out plans by goal - strength, cardio, mobility.'],
      ['Booking calendar', 'Clients pick a slot that syncs to your calendar.'],
      ['Progress tools', 'Share simple check-ins clients can see anytime.'],
    ],
    accent: 'var(--home-accent)',
  },
  {
    match: [],
    tag: '// generated from your prompt',
    brand: 'Yours',
    headlineParts: ['Built from what ', 'you typed'],
    sub: 'This draft comes straight from your description - click anything below to make it yours.',
    cta: 'Get started',
    ctaTop: 'Get started',
    media: 'Generated hero image',
    features: [
      ['Smart layout', 'Sections are picked to fit what your site is for.'],
      ['On-brand palette', 'Colors and type are matched to your description.'],
      ['Ready to edit', 'Click any word, image, or button to change it.'],
    ],
    accent: 'var(--home-teal)',
  },
];

const PLACEHOLDERS = [
  'A cozy bakery site with warm colors and an online ordering menu...',
  'A portfolio for a freelance illustrator, playful and colorful...',
  'A one-page site for my podcast with an email signup...',
  'A booking page for my dog walking business...',
];

const FEATURE_CARDS = [
  {
    title: 'Click and edit',
    description: 'Click directly on any text, image, or button on the page. No separate editor, no mode switching.',
  },
  {
    title: 'Simple color selection',
    description: 'Pick colors from a guided palette so every page you build stays visually consistent automatically.',
  },
  {
    title: 'Unlimited bandwidth',
    description: 'Every paid plan ships with unlimited bandwidth and no surprise overage charges.',
  },
  {
    title: 'Free domain, 1 year',
    description: 'Search and claim a domain in the same flow you build your site in, no separate registrar step.',
  },
  {
    title: 'Traffic booster',
    description: 'Built-in SEO basics and performance checks, so your site is ready to be found before you promote it.',
  },
  {
    title: 'Real support',
    description: 'Support Center and live help when you are stuck, not just a forum of users guessing.',
  },
];

const PRICING_CARDS = [
  {
    name: 'Free',
    description: 'Best for testing ideas and launching your first site',
    price: '$0',
    priceSuffix: '/mo',
    cta: 'Start free',
    ctaHref: '/signup',
    featured: false,
    features: ['AI site generator + click-to-edit', '1 published site', '1GB storage', 'EditPage subdomain'],
  },
  {
    name: 'Growth',
    description: 'The most competitive plan for service businesses and creators',
    price: '$19',
    priceSuffix: '/mo',
    cta: 'Start earning',
    ctaHref: '/signup',
    featured: true,
    badge: 'Most popular',
    socialProof: 'Chosen by 68% of new paying customers',
    annualNote: 'Save 20% with annual billing',
    features: [
      'Everything in Free, plus custom domain',
      'Unlimited pages and traffic',
      'Basic ecommerce and bookings',
      'Email capture and marketing tools',
      'No EditPage badge',
    ],
  },
  {
    name: 'Scale',
    description: 'Built for teams running serious revenue',
    price: '$49',
    priceSuffix: '/mo',
    cta: 'Scale now',
    ctaHref: '/signup',
    featured: false,
    annualNote: 'Save 20% with annual billing',
    features: [
      'Everything in Growth',
      '0% platform transaction fee',
      'Priority support + onboarding',
      'Advanced analytics and automations',
      'Team collaboration access',
    ],
  },
  {
    name: 'Enterprise',
    description: 'For larger brands with advanced needs',
    price: 'Custom',
    priceSuffix: '',
    cta: 'Contact sales',
    ctaHref: '/contact',
    featured: false,
    features: [
      'Everything in Scale',
      'Dedicated success manager',
      'Custom integrations and SSO',
      'Free custom domain, 1 year',
      'Enhanced security controls',
      'Volume-based pricing',
    ],
  },
];

const PREVIEW_DEFAULTS = {
  designPreset: 'clean-saas',
  heroLayout: 'split',
  sectionOrder: ['features', 'stats', 'testimonials', 'faq'],
  navItems: ['Home', 'Services', 'About', 'Contact'],
  stats: [
    ['48h', 'Launch window'],
    ['24/7', 'Support'],
    ['99.9%', 'Uptime'],
  ],
  testimonials: [
    {
      quote: 'The first draft was strong enough to publish with just a few tweaks.',
      author: 'Casey R.',
      role: 'Founder',
    },
    {
      quote: 'It felt like starting from a real website instead of a blank page.',
      author: 'Dana L.',
      role: 'Owner',
    },
  ],
  faq: [
    {
      q: 'Can I edit everything after generating?',
      a: 'Yes. You can change text, layout pieces, and calls-to-action instantly.',
    },
    {
      q: 'Can I publish on my own domain?',
      a: 'Yes. Claim your domain, connect it, and publish when ready.',
    },
  ],
};

const REGEN_DESIGN_PRESETS = ['clean-saas', 'editorial', 'bold-contrast', 'soft-organic'];
const REGEN_HERO_LAYOUTS = ['split', 'centered', 'spotlight'];
const REGEN_CANVAS_TONES = ['light', 'cream', 'dark', 'gradient'];
const STYLE_OPTION_VALUES = {
  background: {
    ivory: '#fffdf8',
    mist: '#eef4ff',
    charcoal: '#151a26',
    sunrise: 'linear-gradient(145deg, #fff2e8 0%, #f5eeff 55%, #ecfbf4 100%)',
  },
  fontFamily: {
    space: "'Space Grotesk', sans-serif",
    inter: "'Inter', sans-serif",
    cormorant: "'Cormorant Garamond', serif",
    mono: "'JetBrains Mono', monospace",
  },
  fontColor: {
    ink: '#1f1a17',
    slate: '#485160',
    snow: '#f8f7f4',
    teal: '#2e9a88',
  },
};

const FONT_FAMILY_OPTIONS = [
  { key: '', label: 'Theme default' },
  { key: 'space', label: 'Space Grotesk' },
  { key: 'inter', label: 'Inter' },
  { key: 'cormorant', label: 'Cormorant Garamond' },
  { key: 'mono', label: 'JetBrains Mono' },
];

const ZOOM_LEVELS = [100, 125, 152, 175, 200];
const GUIDED_TOTAL_STEPS = 4;

const IMAGE_TARGET_LABELS = {
  hero: 'Hero image',
  studio: 'Studio image',
  gallery: 'Gallery image',
};

const GUIDED_PERSONALITIES = [
  { key: 'friendly', label: 'Friendly', promptTone: 'warm, helpful, and welcoming' },
  { key: 'bold', label: 'Bold', promptTone: 'confident, energetic, and punchy' },
  { key: 'luxe', label: 'Luxe', promptTone: 'premium, refined, and sophisticated' },
  { key: 'playful', label: 'Playful', promptTone: 'creative, fun, and imaginative' },
];

const buildGuidedGenerationPrompt = (basePrompt, choices, personalityKey) => {
  const personality = GUIDED_PERSONALITIES.find((item) => item.key === personalityKey) || GUIDED_PERSONALITIES[0];
  const safeBase = String(basePrompt || '').trim();
  const safeChoices = Array.isArray(choices) ? choices.filter(Boolean).slice(0, GUIDED_TOTAL_STEPS) : [];

  if (!safeChoices.length) {
    return `${safeBase}\nStyle personality: ${personality.label} (${personality.promptTone}).`;
  }

  return `${safeBase}\nStyle personality: ${personality.label} (${personality.promptTone}).\nChosen preferences: ${safeChoices.join(' | ')}`;
};

const isSignupHref = (href) => typeof href === 'string' && href.indexOf('/signup') === 0;

const cloneRegionAnswers = (value) => JSON.parse(JSON.stringify(value || {}));

const buildGuidedQuestionFallback = (answers) => {
  const step = Array.isArray(answers) ? (answers.length % 4) : 0;

  if (step === 0) {
    return {
      question: 'What vibe should this website have?',
      options: ['Calm and premium', 'Bold and energetic', 'Playful and colorful', 'Clean and minimal'],
    };
  }

  if (step === 1) {
    return {
      question: 'What should visitors do first?',
      options: ['Book a call', 'Browse products', 'View portfolio', 'Join email list'],
    };
  }

  if (step === 2) {
    return {
      question: 'Which section should lead your page?',
      options: ['Hero with CTA', 'Social proof', 'Feature grid', 'FAQ section'],
    };
  }

  return {
    question: 'Choose your call-to-action tone:',
    options: ['Start now', 'Book now', 'See pricing', 'Talk to us'],
  };
};

const formatRelativeSaveTime = (timestamp) => {
  if (!timestamp) {
    return 'Not saved yet';
  }

  const savedMs = new Date(timestamp).getTime();
  if (Number.isNaN(savedMs)) {
    return 'Not saved yet';
  }

  const deltaSeconds = Math.max(0, Math.floor((Date.now() - savedMs) / 1000));
  if (deltaSeconds < 5) {
    return 'Saved just now';
  }

  if (deltaSeconds < 60) {
    return `Saved ${deltaSeconds}s ago`;
  }

  const deltaMinutes = Math.floor(deltaSeconds / 60);
  if (deltaMinutes < 60) {
    return `Saved ${deltaMinutes}m ago`;
  }

  const deltaHours = Math.floor(deltaMinutes / 60);
  if (deltaHours < 24) {
    return `Saved ${deltaHours}h ago`;
  }

  return `Saved ${new Date(savedMs).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`;
};

const normalizeEditableText = (value, fallback = '') => {
  const cleaned = String(value || '').replace(/\s+/g, ' ').trim();
  return cleaned || String(fallback || '').trim();
};

const EditableText = ({
  as = 'span',
  className,
  value,
  onCommit,
  onDirty,
}) => {
  const Component = as;
  const dirtyRef = React.useRef(false);

  const handleInput = (event) => {
    if (!dirtyRef.current && typeof onDirty === 'function') {
      onDirty();
      dirtyRef.current = true;
    }

    event.currentTarget.setAttribute('data-editing', 'true');
  };

  const handleBlur = (event) => {
    const nextValue = normalizeEditableText(event.currentTarget.textContent, value);
    const prevValue = normalizeEditableText(value);

    if (nextValue !== prevValue && typeof onCommit === 'function') {
      onCommit(nextValue);
    }

    event.currentTarget.setAttribute('data-editing', 'false');
    dirtyRef.current = false;
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.currentTarget.blur();
    }
  };

  return (
    <Component
      className={`editable-text ${className || ''}`.trim()}
      contentEditable
      suppressContentEditableWarning
      spellCheck={false}
      onInput={handleInput}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      data-editing="false"
    >
      {value}
    </Component>
  );
};

const REGEN_SECTION_ORDERS = [
  ['features', 'stats', 'testimonials', 'faq'],
  ['testimonials', 'features', 'faq', 'stats'],
  ['stats', 'faq', 'features', 'testimonials'],
  ['faq', 'features', 'stats', 'testimonials'],
];
const REGEN_ACCENTS = ['var(--home-accent)', 'var(--home-lavender)', 'var(--home-teal)'];

const buildImageSet = (promptText, seed = 'base') => {
  const promptSeed = encodeURIComponent(`${promptText || 'homepage'}-${seed}`);
  const imageUrl = (slot, width, height) => `https://picsum.photos/seed/${promptSeed}-${slot}/${width}/${height}`;

  return {
    hero: imageUrl('hero', 1200, 760),
    studio: imageUrl('studio', 920, 1100),
    gallery: [
      imageUrl('gallery-1', 900, 1100),
      imageUrl('gallery-2', 900, 700),
      imageUrl('gallery-3', 900, 700),
      imageUrl('gallery-4', 900, 700),
      imageUrl('gallery-5', 1200, 760),
      imageUrl('gallery-6', 1200, 760),
    ],
  };
};

const hashSeed = (seed) => {
  const raw = String(seed || 'default');
  let hash = 0;

  for (let i = 0; i < raw.length; i += 1) {
    hash = (hash * 31 + raw.charCodeAt(i)) >>> 0;
  }

  return hash;
};

const rotateValue = (list, seed, fallback) => {
  if (!Array.isArray(list) || !list.length) {
    return fallback;
  }

  const index = hashSeed(seed) % list.length;
  return list[index];
};

const hexToRgb = (hex) => {
  const normalized = String(hex || '').trim().replace('#', '');
  if (!/^[0-9a-fA-F]{6}$/.test(normalized)) {
    return null;
  }

  return {
    r: parseInt(normalized.slice(0, 2), 16),
    g: parseInt(normalized.slice(2, 4), 16),
    b: parseInt(normalized.slice(4, 6), 16),
  };
};

const channelToLinear = (channel) => {
  const normalized = channel / 255;
  return normalized <= 0.04045
    ? normalized / 12.92
    : ((normalized + 0.055) / 1.055) ** 2.4;
};

const relativeLuminance = (hex) => {
  const rgb = hexToRgb(hex);
  if (!rgb) {
    return 0;
  }

  return (0.2126 * channelToLinear(rgb.r))
    + (0.7152 * channelToLinear(rgb.g))
    + (0.0722 * channelToLinear(rgb.b));
};

const contrastRatio = (foregroundHex, backgroundHex) => {
  const l1 = relativeLuminance(foregroundHex);
  const l2 = relativeLuminance(backgroundHex);
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  return (lighter + 0.05) / (darker + 0.05);
};

const BASE_TONE_TOKENS = {
  light: {
    bg: '#ffffff',
    text: '#2d2520',
    textDim: '#6f6259',
    textFaint: '#8f7f73',
    border: '#dfd3c6',
    borderSoft: '#e8ddd1',
    teal: '#2e9a88',
    lavender: '#8f6aa5',
  },
  cream: {
    bg: '#fff4e6',
    text: '#2f261f',
    textDim: '#6d6158',
    textFaint: '#8a7b6e',
    border: '#ddcfbf',
    borderSoft: '#e5d9cb',
    teal: '#308f80',
    lavender: '#8a669f',
  },
  gradient: {
    bg: '#f2f8ff',
    text: '#2a2132',
    textDim: '#655a72',
    textFaint: '#877b95',
    border: '#cfc8e0',
    borderSoft: '#d7d4e7',
    teal: '#2f9d90',
    lavender: '#7f5dcb',
  },
  dark: {
    bg: '#141722',
    text: '#f3f1ec',
    textDim: '#a1a5b2',
    textFaint: '#777c8d',
    border: '#353846',
    borderSoft: '#2a2d36',
    teal: '#66d7c1',
    lavender: '#af9bee',
  },
};

const ensureReadableToneTokens = (tone) => {
  const selected = BASE_TONE_TOKENS[tone] || BASE_TONE_TOKENS.light;
  const textRatio = contrastRatio(selected.text, selected.bg);
  const dimRatio = contrastRatio(selected.textDim, selected.bg);

  if (textRatio >= 4.5 && dimRatio >= 3) {
    return selected;
  }

  if (tone === 'dark') {
    return {
      ...selected,
      text: '#f6f4ef',
      textDim: '#bcc1d1',
      textFaint: '#9ca2b4',
    };
  }

  return {
    ...selected,
    text: '#241d18',
    textDim: '#4f453e',
    textFaint: '#6b5f56',
  };
};

const isLightBackgroundValue = (backgroundValue, fallbackHex = '#ffffff') => {
  const raw = String(backgroundValue || '').trim();

  if (!raw) {
    return relativeLuminance(fallbackHex) >= 0.62;
  }

  if (/^#[0-9a-fA-F]{6}$/.test(raw)) {
    return relativeLuminance(raw) >= 0.62;
  }

  if (raw.indexOf('gradient') !== -1) {
    const hexMatches = raw.match(/#[0-9a-fA-F]{6}/g) || [];
    if (!hexMatches.length) {
      return relativeLuminance(fallbackHex) >= 0.62;
    }

    let total = 0;
    for (let i = 0; i < hexMatches.length; i += 1) {
      total += relativeLuminance(hexMatches[i]);
    }

    return (total / hexMatches.length) >= 0.6;
  }

  return relativeLuminance(fallbackHex) >= 0.62;
};

const enforceDarkTextOnLightBackground = (styleObject, fallbackBackground) => {
  const style = { ...(styleObject || {}) };
  const effectiveBackground = typeof style.background === 'string' && style.background.trim()
    ? style.background
    : fallbackBackground;

  if (!isLightBackgroundValue(effectiveBackground, fallbackBackground)) {
    return style;
  }

  style.color = '#241d18';
  style['--home-text'] = '#241d18';
  style['--home-text-dim'] = '#4f453e';
  style['--home-text-faint'] = '#6b5f56';
  return style;
};

const buildRegionStyleFromChoices = (choices = {}) => {
  const style = {};
  const customBackground = typeof choices.customBackground === 'string' ? choices.customBackground.trim() : '';
  const customFontColor = typeof choices.customFontColor === 'string' ? choices.customFontColor.trim() : '';
  const backgroundValue = STYLE_OPTION_VALUES.background[choices.background];
  const fontValue = STYLE_OPTION_VALUES.fontFamily[choices.fontFamily];
  const textValue = STYLE_OPTION_VALUES.fontColor[choices.fontColor];

  if (/^#[0-9a-fA-F]{6}$/.test(customBackground)) {
    style.background = customBackground;
  } else if (backgroundValue) {
    style.background = backgroundValue;
  }

  if (fontValue) {
    style.fontFamily = fontValue;
  }

  if (/^#[0-9a-fA-F]{6}$/.test(customFontColor)) {
    style.color = customFontColor;
    style['--home-text'] = customFontColor;
    style['--home-text-dim'] = customFontColor;
    style['--home-text-faint'] = customFontColor;
  } else if (textValue) {
    style.color = textValue;
    style['--home-text'] = textValue;
    style['--home-text-dim'] = textValue;
    style['--home-text-faint'] = textValue;
  }

  return style;
};

const normalizeCanvasTone = (tone) => {
  if (tone === 'light' || tone === 'cream' || tone === 'dark' || tone === 'gradient') {
    return tone;
  }

  return null;
};

const pickCanvasTone = (promptText, designPreset, seed = '') => {
  const text = String(promptText || '').toLowerCase();

  if (/(dark|night|midnight|moody|black|neon|luxury)/.test(text)) {
    return 'dark';
  }

  if (/(gradient|vibrant|sunset|rainbow|colorful|playful)/.test(text)) {
    return 'gradient';
  }

  if (/(white background|pure white|bright white|light mode|airy and bright)/.test(text)) {
    return rotateValue(['light', 'cream'], `${text}|${seed}|explicit-light`, 'light');
  }

  if (/(clean|minimal|airy|soft|organic|neutral)/.test(text)) {
    return rotateValue(['cream', 'gradient', 'dark'], `${text}|${designPreset}|${seed}|balanced-clean`, 'cream');
  }

  const designToneMap = {
    'clean-saas': ['light', 'cream', 'gradient'],
    editorial: ['cream', 'gradient', 'dark'],
    'bold-contrast': ['dark', 'gradient', 'cream'],
    'soft-organic': ['cream', 'gradient', 'dark'],
  };

  const tonePool = designToneMap[designPreset] || REGEN_CANVAS_TONES;
  if (/(light|white)/.test(text)) {
    return rotateValue(['light', 'cream', 'gradient'], `${text}|${designPreset}|${seed}|lightish`, 'cream');
  }

  return rotateValue(tonePool, `${text}|${designPreset}|${seed}|tone`, 'cream');
};

const buildRegeneratedVariant = (baseScene, promptText, seed) => {
  const variantSeed = `${promptText || ''}|${seed || ''}`;
  const headlineLeads = ['Reimagined for ', 'A new direction for ', 'Alternative concept for '];
  const ctaVariants = ['Book now', 'Start this design', 'Launch this concept'];
  const subVariants = [
    'A fresh concept generated from your prompt with a different visual direction.',
    'New layout, hierarchy, and tone generated from the same prompt.',
    'A second concept pass with distinct storytelling and structure.',
  ];

  return {
    ...baseScene,
    tag: '// regenerated variation from your prompt',
    designPreset: rotateValue(REGEN_DESIGN_PRESETS, `${variantSeed}:preset`, 'clean-saas'),
    heroLayout: rotateValue(REGEN_HERO_LAYOUTS, `${variantSeed}:layout`, 'split'),
    canvasTone: rotateValue(REGEN_CANVAS_TONES, `${variantSeed}:tone`, baseScene.canvasTone || 'light'),
    sectionOrder: rotateValue(REGEN_SECTION_ORDERS, `${variantSeed}:sections`, REGEN_SECTION_ORDERS[0]),
    accent: rotateValue(REGEN_ACCENTS, `${variantSeed}:accent`, baseScene.accent || 'var(--home-teal)'),
    headlineParts: [
      rotateValue(headlineLeads, `${variantSeed}:headlineLead`, 'Reimagined for '),
      baseScene.brand || 'your brand',
    ],
    sub: `${baseScene.sub || ''} ${rotateValue(subVariants, `${variantSeed}:sub`, '')}`.trim(),
    cta: rotateValue(ctaVariants, `${variantSeed}:cta`, baseScene.cta || 'Get started'),
  };
};

const pickScene = (promptText) => {
  const text = (promptText || '').toLowerCase();
  for (let i = 0; i < SCENES.length - 1; i += 1) {
    const scene = SCENES[i];
    for (let j = 0; j < scene.match.length; j += 1) {
      if (text.indexOf(scene.match[j]) !== -1) {
        return scene;
      }
    }
  }

  return SCENES[SCENES.length - 1];
};

const HomepageContent = ({ domainName, handleOnChange, handleKeyDown, handleDomainOpen }) => {
  const [promptValue, setPromptValue] = React.useState('');
  const [promptPlaceholder, setPromptPlaceholder] = React.useState(PLACEHOLDERS[0]);
  const [statusText, setStatusText] = React.useState('Describe your site above, then let WebBot shape your first draft');
  const [isGenerating, setIsGenerating] = React.useState(false);
  const [isRegenerating, setIsRegenerating] = React.useState(false);
  const [hasGenerated, setHasGenerated] = React.useState(false);
  const [scene, setScene] = React.useState(SCENES[SCENES.length - 1]);
  const [lastScene, setLastScene] = React.useState(null);
  const [lastPromptUsed, setLastPromptUsed] = React.useState('');
  const [generationMeta, setGenerationMeta] = React.useState({
    source: 'template',
    modelUsed: null,
    usedFallback: false,
  });
  const [activeRegion, setActiveRegion] = React.useState('global');
  const [regionStyleAnswers, setRegionStyleAnswers] = React.useState({
    global: {},
  });
  const [previewDevice, setPreviewDevice] = React.useState('desktop');
  const [zoomLevel, setZoomLevel] = React.useState(100);
  const [isPreviewFullscreen, setIsPreviewFullscreen] = React.useState(false);
  const [canUndo, setCanUndo] = React.useState(false);
  const [canRedo, setCanRedo] = React.useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = React.useState(false);
  const [hasUnsavedTextEdits, setHasUnsavedTextEdits] = React.useState(false);
  const [lastSavedAt, setLastSavedAt] = React.useState(null);
  const [saveTimeTick, setSaveTimeTick] = React.useState(0);
  const [guidedBasePrompt, setGuidedBasePrompt] = React.useState('');
  const [guidedChoices, setGuidedChoices] = React.useState([]);
  const [guidedPersonality, setGuidedPersonality] = React.useState('friendly');
  const [guidedQuestion, setGuidedQuestion] = React.useState('Generate your site to start the fun setup questions.');
  const [guidedOptions, setGuidedOptions] = React.useState([]);
  const [isGuidedLoading, setIsGuidedLoading] = React.useState(false);
  const [isGuidedQuestionVisible, setIsGuidedQuestionVisible] = React.useState(true);
  const [typedGuidedQuestion, setTypedGuidedQuestion] = React.useState('');
  const [imageSet, setImageSet] = React.useState(buildImageSet('homepage', 'initial'));
  const [selectedImageTarget, setSelectedImageTarget] = React.useState(null);
  const [customImageOverrides, setCustomImageOverrides] = React.useState({});
  const [customLogoDataUrl, setCustomLogoDataUrl] = React.useState('');
  const [isFading, setIsFading] = React.useState(false);

  const canvasRef = React.useRef(null);
  const promptInputRef = React.useRef(null);
  const styleHistoryRef = React.useRef([cloneRegionAnswers({ global: {} })]);
  const historyIndexRef = React.useRef(0);
  const saveFloorIndexRef = React.useRef(0);

  const syncHistoryFlags = React.useCallback(() => {
    const floor = saveFloorIndexRef.current;
    const index = historyIndexRef.current;
    setCanUndo(index > floor);
    setCanRedo(index < styleHistoryRef.current.length - 1);
    setHasUnsavedChanges(index !== floor);
  }, []);

  const markTextEditDirty = React.useCallback(() => {
    setHasUnsavedTextEdits(true);
  }, []);

  const applySceneTextChange = React.useCallback((updater) => {
    setScene((prev) => updater(prev));
    setHasUnsavedTextEdits(true);
  }, []);

  const saveGuestDraft = React.useCallback((draftScene, draftPrompt) => {
    try {
      localStorage.setItem(
        GUEST_DRAFT_STORAGE_KEY,
        JSON.stringify({
          prompt: draftPrompt,
          scene: draftScene,
          createdAt: new Date().toISOString(),
        })
      );
    } catch (error) {
      // Ignore storage failures and keep the in-memory flow working.
    }
  }, []);

  React.useEffect(() => {
    const interval = window.setInterval(() => {
      const active = document.activeElement;
      const isPromptFocused = active && active.id === 'promptInput';

      if (!isPromptFocused && !promptValue) {
        setPromptPlaceholder(prev => {
          const nextIndex = (PLACEHOLDERS.indexOf(prev) + 1) % PLACEHOLDERS.length;
          return PLACEHOLDERS[nextIndex];
        });
      }
    }, 3200);

    return () => window.clearInterval(interval);
  }, [promptValue]);

  React.useEffect(() => {
    if (!lastSavedAt) {
      return undefined;
    }

    const interval = window.setInterval(() => {
      setSaveTimeTick((prev) => prev + 1);
    }, 30000);

    return () => window.clearInterval(interval);
  }, [lastSavedAt]);

  React.useEffect(() => {
    try {
      const rawSavedState = localStorage.getItem(EDITOR_SAVE_STATE_STORAGE_KEY);
      if (!rawSavedState) {
        return;
      }

      const parsed = JSON.parse(rawSavedState);
      if (!parsed || typeof parsed !== 'object') {
        return;
      }

      if (parsed.savedAt) {
        setLastSavedAt(parsed.savedAt);
      }
    } catch (error) {
      // Ignore malformed persisted editor state.
    }
  }, [syncHistoryFlags]);

  React.useEffect(() => {
    if (!hasGenerated || !isGuidedQuestionVisible || isGuidedLoading) {
      return;
    }

    const fullText = String(guidedQuestion || '').trim();
    setTypedGuidedQuestion('');

    if (!fullText) {
      return;
    }

    let index = 0;
    const timer = window.setInterval(() => {
      index += 1;
      setTypedGuidedQuestion(fullText.slice(0, index));

      if (index >= fullText.length) {
        window.clearInterval(timer);
      }
    }, 16);

    return () => window.clearInterval(timer);
  }, [guidedQuestion, hasGenerated, isGuidedLoading, isGuidedQuestionVisible]);

  const fetchGuidedQuestion = React.useCallback(async (basePrompt, answers, personalityKey = guidedPersonality) => {
    if (!basePrompt || basePrompt.length < 3) {
      const fallback = buildGuidedQuestionFallback(answers);
      setGuidedQuestion(fallback.question);
      setGuidedOptions(fallback.options);
      setIsGuidedQuestionVisible(true);
      return;
    }

    setIsGuidedLoading(true);

    const personality = GUIDED_PERSONALITIES.find((item) => item.key === personalityKey) || GUIDED_PERSONALITIES[0];
    const fallback = buildGuidedQuestionFallback(answers);
    const backendUrl = process.env.REACT_APP_BACKEND_URL;

    if (!backendUrl) {
      setGuidedQuestion(fallback.question);
      setGuidedOptions(fallback.options);
      setIsGuidedLoading(false);
      setIsGuidedQuestionVisible(true);
      return;
    }

    try {
      const response = await fetch(`${backendUrl}/website/generate-question`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          prompt: basePrompt,
          answers: [...answers, `Personality: ${personality.label}`],
        }),
        mode: 'cors',
      });

      if (!response.ok) {
        throw new Error(response.statusText || 'Unable to load guided question');
      }

      const payload = await response.json();
      const data = payload && payload.data ? payload.data : null;
      const question = data && typeof data.question === 'string' ? data.question.trim() : '';
      const options = data && Array.isArray(data.options) ? data.options.filter(Boolean).slice(0, 4) : [];

      if (!question || options.length !== 4) {
        throw new Error('Invalid guided question response');
      }

      setGuidedQuestion(question);
      setGuidedOptions(options);
    } catch (error) {
      setGuidedQuestion(fallback.question);
      setGuidedOptions(fallback.options);
    } finally {
      setIsGuidedLoading(false);
      setIsGuidedQuestionVisible(true);
    }
  }, [guidedPersonality]);

  const runGenerate = React.useCallback(async (fromRegen = false, sourceText = null, options = {}) => {
    const text = (sourceText !== null ? sourceText : promptValue).trim();
    const variationSeed = options && options.variationSeed ? String(options.variationSeed) : '';
    const promptForGuided = options && options.guidedBasePrompt ? String(options.guidedBasePrompt) : text;
    const guidedAnswerSnapshot = options && Array.isArray(options.guidedAnswers) ? options.guidedAnswers.slice(0, 8) : [];
    const beforeScene = scene;

    if (!text && !fromRegen) {
      setStatusText('Type a few words about your site first');
      return;
    }

    const chosen = fromRegen && lastScene ? lastScene : pickScene(text);
    setLastScene(chosen);

    setIsGenerating(true);
    setStatusText('Building your homepage...');
    setLastPromptUsed(text);

    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    let generatedScene = chosen;
    let meta = {
      source: 'template',
      modelUsed: null,
      usedFallback: false,
    };

    if (backendUrl) {
      try {
        const response = await fetch(`${backendUrl}/website/generate`, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ prompt: text, variationSeed }),
          mode: 'cors',
        });

        if (!response.ok) {
          throw new Error(response.statusText || 'Unable to generate draft');
        }

        const payload = await response.json();

        if (payload && payload.data && payload.data.scene) {
          generatedScene = payload.data.scene;
          setLastScene(payload.data.scene);
          meta = payload.data.generationMeta || meta;
        }
      } catch (error) {
        meta = {
          source: 'template',
          modelUsed: null,
          usedFallback: true,
        };
      }
    } else {
      meta = {
        source: 'template',
        modelUsed: null,
        usedFallback: true,
      };
    }

    const beforeSignature = JSON.stringify({
      brand: beforeScene.brand,
      headlineParts: beforeScene.headlineParts,
      sub: beforeScene.sub,
      designPreset: beforeScene.designPreset,
      heroLayout: beforeScene.heroLayout,
      sectionOrder: beforeScene.sectionOrder,
      accent: beforeScene.accent,
    });
    const afterSignature = JSON.stringify({
      brand: generatedScene.brand,
      headlineParts: generatedScene.headlineParts,
      sub: generatedScene.sub,
      designPreset: generatedScene.designPreset,
      heroLayout: generatedScene.heroLayout,
      sectionOrder: generatedScene.sectionOrder,
      accent: generatedScene.accent,
    });

    if (fromRegen || beforeSignature === afterSignature) {
      generatedScene = buildRegeneratedVariant(
        generatedScene,
        text,
        variationSeed || `${Date.now()}-${Math.random()}`
      );
      meta = {
        ...meta,
        forcedVariant: true,
      };
    }

    const resolvedTone = normalizeCanvasTone(generatedScene.canvasTone)
      || pickCanvasTone(text, generatedScene.designPreset, variationSeed || generatedScene.heroLayout || 'primary');
    generatedScene = {
      ...generatedScene,
      canvasTone: resolvedTone,
    };

    // Start each newly generated draft with AI-driven theme colors, not previous manual picker overrides.
    const freshStyleAnswers = { global: {} };
    setRegionStyleAnswers(freshStyleAnswers);
    styleHistoryRef.current = [cloneRegionAnswers(freshStyleAnswers)];
    historyIndexRef.current = 0;
    saveFloorIndexRef.current = 0;
    setCanUndo(false);
    setCanRedo(false);
    setHasUnsavedChanges(false);
    setHasUnsavedTextEdits(false);

    setScene(generatedScene);
    setImageSet(buildImageSet(text, variationSeed || generatedScene.designPreset || 'primary'));
    setCustomImageOverrides({});
    setCustomLogoDataUrl('');
    setSelectedImageTarget(null);
    setGenerationMeta(meta);
    setHasGenerated(true);
    setIsGenerating(false);
    if (meta.forcedVariant) {
      setStatusText('Regenerated - alternate design concept loaded');
    } else {
      setStatusText(meta.usedFallback
        ? 'Draft ready - generated with local fallback logic from your prompt'
        : 'Draft ready - generated by Claude from your prompt');
    }
    saveGuestDraft(generatedScene, text);

    if (promptForGuided && promptForGuided.length >= 3) {
      setGuidedBasePrompt(promptForGuided);
      setGuidedChoices(guidedAnswerSnapshot);
      fetchGuidedQuestion(promptForGuided, guidedAnswerSnapshot);
    }

    if (canvasRef.current) {
      canvasRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [fetchGuidedQuestion, lastScene, promptValue, saveGuestDraft, scene]);

  const onPromptSubmit = (event) => {
    event.preventDefault();
    const typedPrompt = promptValue.trim();
    setGuidedChoices([]);
    runGenerate(false, buildGuidedGenerationPrompt(typedPrompt, [], guidedPersonality), {
      guidedBasePrompt: typedPrompt,
      guidedAnswers: [],
    });
  };

  const onPromptInputKeyDown = (event) => {
    if (event.key !== 'Enter' || event.shiftKey || (event.nativeEvent && event.nativeEvent.isComposing)) {
      return;
    }

    event.preventDefault();
    onPromptSubmit(event);
  };

  const onRegenerate = async () => {
    if (isGenerating || isRegenerating) {
      return;
    }

    const basePrompt = (promptValue || '').trim();

    if (!basePrompt) {
      setStatusText('Type a prompt first, then click regenerate.');
      if (promptInputRef.current) {
        promptInputRef.current.focus();
      }
      return;
    }

    setIsRegenerating(true);
    setIsFading(true);

    await runGenerate(true, basePrompt, {
      variationSeed: `${Date.now()}-${Math.random()}`,
      guidedBasePrompt: guidedBasePrompt || basePrompt,
      guidedAnswers: guidedChoices,
    });

    window.setTimeout(() => {
      setIsFading(false);
      setIsRegenerating(false);
    }, 220);
  };

  const onPublishSubmit = (event) => {
    event.preventDefault();
    handleDomainOpen();
  };

  const onContinueToSignup = () => {
    if (SIGNUP_DISABLED) {
      setStatusText('Signup is temporarily disabled.');
      return;
    }

    saveGuestDraft(scene, promptValue.trim());
    window.location.assign('/signup?from=generator');
  };

  const onPricingCtaClick = (href) => {
    if (!href) {
      return;
    }

    if (SIGNUP_DISABLED && isSignupHref(href)) {
      setStatusText('Signup is temporarily disabled.');
      return;
    }

    window.location.assign(href);
  };

  const onGuidedOptionClick = async (option) => {
    const basePrompt = (guidedBasePrompt || promptValue || '').trim();

    if (!basePrompt || !option || isGenerating) {
      return;
    }

    const nextChoices = [...guidedChoices, option].slice(-GUIDED_TOTAL_STEPS);
    const enrichedPrompt = buildGuidedGenerationPrompt(basePrompt, nextChoices, guidedPersonality);

    setGuidedChoices(nextChoices);
    setIsGuidedQuestionVisible(false);
    await runGenerate(false, enrichedPrompt, {
      guidedBasePrompt: basePrompt,
      guidedAnswers: nextChoices,
      variationSeed: `${Date.now()}-${Math.random()}`,
    });
  };

  const onCanvasMouseMove = React.useCallback((event) => {
    const target = event.target;
    if (!(target instanceof Element)) {
      return;
    }

    const sectionNode = target.closest('[data-region]');
    const nextRegion = sectionNode ? sectionNode.getAttribute('data-region') : 'global';
    if (nextRegion && nextRegion !== activeRegion) {
      setActiveRegion(nextRegion);
    }
  }, [activeRegion]);

  const onCanvasMouseLeave = React.useCallback(() => {
    setActiveRegion('global');
  }, []);

  const applyGlobalStyleValue = React.useCallback((key, value) => {
    if (!hasGenerated) {
      return;
    }

    setRegionStyleAnswers((prev) => {
      const nextAnswers = {
        ...prev,
        global: {
          ...(prev.global || {}),
          [key]: value,
        },
      };
      const snapshot = cloneRegionAnswers(nextAnswers);
      const history = styleHistoryRef.current.slice(0, historyIndexRef.current + 1);
      history.push(snapshot);
      styleHistoryRef.current = history;
      historyIndexRef.current = styleHistoryRef.current.length - 1;
      syncHistoryFlags();
      return snapshot;
    });
  }, [hasGenerated, syncHistoryFlags]);

  const onUndoStyleChange = React.useCallback(() => {
    if (!hasGenerated) {
      return;
    }

    if (historyIndexRef.current <= saveFloorIndexRef.current) {
      return;
    }

    historyIndexRef.current -= 1;
    const snapshot = cloneRegionAnswers(styleHistoryRef.current[historyIndexRef.current]);
    setRegionStyleAnswers(snapshot);
    syncHistoryFlags();
  }, [hasGenerated, syncHistoryFlags]);

  const onRedoStyleChange = React.useCallback(() => {
    if (!hasGenerated) {
      return;
    }

    if (historyIndexRef.current >= styleHistoryRef.current.length - 1) {
      return;
    }

    historyIndexRef.current += 1;
    const snapshot = cloneRegionAnswers(styleHistoryRef.current[historyIndexRef.current]);
    setRegionStyleAnswers(snapshot);
    syncHistoryFlags();
  }, [hasGenerated, syncHistoryFlags]);

  const onSaveEditorChanges = React.useCallback(() => {
    if (!hasGenerated) {
      return;
    }

    const savedAt = new Date().toISOString();
    const snapshot = cloneRegionAnswers(regionStyleAnswers);
    saveFloorIndexRef.current = historyIndexRef.current;
    setLastSavedAt(savedAt);
    setHasUnsavedTextEdits(false);
    setSaveTimeTick((prev) => prev + 1);
    syncHistoryFlags();

    try {
      localStorage.setItem(
        EDITOR_SAVE_STATE_STORAGE_KEY,
        JSON.stringify({
          savedAt,
        })
      );
    } catch (error) {
      // Ignore persistence failures and keep save checkpoint behavior in-memory.
    }
  }, [hasGenerated, syncHistoryFlags]);

  const isSaveDirty = hasUnsavedChanges || hasUnsavedTextEdits;

  const saveMetaLabel = React.useMemo(() => {
    if (!hasGenerated) {
      return 'Generate to enable guided remix';
    }

    if (isSaveDirty) {
      return 'Unsaved changes';
    }

    return formatRelativeSaveTime(lastSavedAt);
  }, [hasGenerated, isSaveDirty, lastSavedAt, saveTimeTick]);

  const cycleZoom = React.useCallback(() => {
    const currentIndex = ZOOM_LEVELS.indexOf(zoomLevel);
    const nextIndex = currentIndex === -1 ? 0 : (currentIndex + 1) % ZOOM_LEVELS.length;
    setZoomLevel(ZOOM_LEVELS[nextIndex]);
  }, [zoomLevel]);

  const sceneNavItems = Array.isArray(scene.navItems) && scene.navItems.length === 4
    ? scene.navItems
    : PREVIEW_DEFAULTS.navItems;
  const sceneStats = Array.isArray(scene.stats) && scene.stats.length === 3
    ? scene.stats
    : PREVIEW_DEFAULTS.stats;
  const sceneTestimonials = Array.isArray(scene.testimonials) && scene.testimonials.length === 2
    ? scene.testimonials
    : PREVIEW_DEFAULTS.testimonials;
  const sceneFaq = Array.isArray(scene.faq) && scene.faq.length === 2
    ? scene.faq
    : PREVIEW_DEFAULTS.faq;
  const sceneDesignPreset = typeof scene.designPreset === 'string' ? scene.designPreset : PREVIEW_DEFAULTS.designPreset;
  const sceneHeroLayout = typeof scene.heroLayout === 'string' ? scene.heroLayout : PREVIEW_DEFAULTS.heroLayout;
  const sceneCanvasTone = hasGenerated
    ? (normalizeCanvasTone(scene.canvasTone)
      || pickCanvasTone(lastPromptUsed || promptValue, sceneDesignPreset, sceneHeroLayout))
    : 'dark';
  const editableRegionAnswers = hasGenerated ? regionStyleAnswers : { global: {} };
  const getRegionStyle = (regionKey) => {
    const base = buildRegionStyleFromChoices(editableRegionAnswers.global || {});
    const merged = regionKey === 'global'
      ? base
      : {
      ...base,
      ...buildRegionStyleFromChoices(editableRegionAnswers[regionKey] || {}),
    };

    if (activeRegion === regionKey && regionKey !== 'global') {
      return {
        ...merged,
        boxShadow: 'inset 0 0 0 1px rgba(95, 205, 184, 0.45)',
      };
    }

    return merged;
  };
  const toneTokens = ensureReadableToneTokens(sceneCanvasTone);
  const canvasToneStyles = {
    '--home-text': toneTokens.text,
    '--home-text-dim': toneTokens.textDim,
    '--home-text-faint': toneTokens.textFaint,
    '--home-border': toneTokens.border,
    '--home-border-soft': toneTokens.borderSoft,
    '--home-teal': toneTokens.teal,
    '--home-lavender': toneTokens.lavender,
  };
  const globalRegionStyle = enforceDarkTextOnLightBackground(getRegionStyle('global'), toneTokens.bg);
  const canvasStyle = {
    ...canvasToneStyles,
    ...globalRegionStyle,
    transform: `scale(${zoomLevel / 100})`,
    transformOrigin: 'top left',
    width: `${100 / (zoomLevel / 100)}%`,
  };
  const sceneSectionOrder = Array.isArray(scene.sectionOrder) && scene.sectionOrder.length === 4
    ? scene.sectionOrder
    : PREVIEW_DEFAULTS.sectionOrder;
  const canvasVariantClass = `design-${sceneDesignPreset} tone-${sceneCanvasTone} layout-${sceneHeroLayout}`;
  const globalChoices = editableRegionAnswers.global || {};
  const panelBackgroundColor = /^#[0-9a-fA-F]{6}$/.test(globalChoices.customBackground || '')
    ? globalChoices.customBackground
    : toneTokens.bg;
  const panelFontColor = /^#[0-9a-fA-F]{6}$/.test(globalChoices.customFontColor || '')
    ? globalChoices.customFontColor
    : toneTokens.text;
  const editorialGalleryLabels = [
    `${scene.brand} story`,
    scene.features[0][0],
    scene.features[1][0],
    scene.features[2][0],
    sceneTestimonials[0].author,
    sceneTestimonials[1].author,
  ];
  const editorialServiceItems = [
    ...scene.features,
    [sceneFaq[0].q, sceneFaq[0].a],
    [sceneFaq[1].q, sceneFaq[1].a],
  ];
  const resolvedHeroImage = customImageOverrides.hero || imageSet.hero;
  const resolvedStudioImage = customImageOverrides.studio || imageSet.studio;
  const resolvedGalleryImages = React.useMemo(() => {
    const base = Array.isArray(imageSet.gallery) ? [...imageSet.gallery] : [];
    const galleryOverrides = customImageOverrides.gallery || {};

    Object.keys(galleryOverrides).forEach((key) => {
      const index = Number(key);
      if (!Number.isNaN(index) && index >= 0) {
        base[index] = galleryOverrides[key];
      }
    });

    return base;
  }, [customImageOverrides.gallery, imageSet.gallery]);
  const galleryPickerOptions = React.useMemo(() => {
    const rawSeed = String(lastPromptUsed || promptValue || scene.brand || 'homepage')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'homepage';
    const generated = Array.from({ length: 12 }).map((_, index) => (
      `https://picsum.photos/seed/${rawSeed}-picker-${index + 1}/520/340`
    ));
    const merged = [imageSet.hero, imageSet.studio, ...(imageSet.gallery || []), ...generated].filter(Boolean);
    return [...new Set(merged)].slice(0, 8);
  }, [imageSet.gallery, imageSet.hero, imageSet.studio, lastPromptUsed, promptValue, scene.brand]);

  const onImageTargetClick = React.useCallback((target, index = null) => {
    setSelectedImageTarget((prev) => {
      if (prev && prev.target === target && prev.index === index) {
        return null;
      }

      return { target, index };
    });
  }, []);

  const onGalleryImagePick = React.useCallback((imageUrl) => {
    if (!hasGenerated || !selectedImageTarget || !imageUrl) {
      return;
    }

    setCustomImageOverrides((prev) => {
      if (selectedImageTarget.target === 'hero') {
        return {
          ...prev,
          hero: imageUrl,
        };
      }

      if (selectedImageTarget.target === 'studio') {
        return {
          ...prev,
          studio: imageUrl,
        };
      }

      if (selectedImageTarget.target === 'gallery' && Number.isInteger(selectedImageTarget.index) && selectedImageTarget.index >= 0) {
        return {
          ...prev,
          gallery: {
            ...(prev.gallery || {}),
            [selectedImageTarget.index]: imageUrl,
          },
        };
      }

      return prev;
    });

    setHasUnsavedTextEdits(true);
    setSelectedImageTarget(null);
  }, [hasGenerated, selectedImageTarget]);

  const onLogoFileChange = React.useCallback((event) => {
    const file = event.target && event.target.files && event.target.files[0] ? event.target.files[0] : null;
    if (!file) {
      return;
    }

    if (!file.type || file.type.indexOf('image/') !== 0) {
      setStatusText('Please upload an image file for your logo.');
      event.target.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const result = typeof reader.result === 'string' ? reader.result : '';
      if (!result) {
        setStatusText('Could not read that logo file. Try another image.');
        event.target.value = '';
        return;
      }

      setCustomLogoDataUrl(result);
      setHasUnsavedTextEdits(true);
      setStatusText('Logo uploaded.');
      event.target.value = '';
    };
    reader.onerror = () => {
      setStatusText('Could not read that logo file. Try another image.');
      event.target.value = '';
    };

    reader.readAsDataURL(file);
  }, []);

  const onClearUploadedLogo = React.useCallback(() => {
    if (!customLogoDataUrl) {
      return;
    }

    setCustomLogoDataUrl('');
    setHasUnsavedTextEdits(true);
    setStatusText('Logo removed.');
  }, [customLogoDataUrl]);

  const renderGeneratedSection = (sectionKey) => {
    if (sectionKey === 'features') {
      return (
        <div className="feature-row" key="features" data-region="features" style={getRegionStyle('features')}>
          {scene.features.map((feature, index) => (
            <div className="feature" key={feature[0]}>
              <div className="feature-icon">
                {index === 0 ? (
                  <svg className="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2" /></svg>
                ) : null}
                {index === 1 ? (
                  <svg className="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="9" /></svg>
                ) : null}
                {index === 2 ? (
                  <svg className="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2l9 5-9 5-9-5 9-5z" /></svg>
                ) : null}
              </div>
              <EditableText
                as="h5"
                className={isFading ? 'gen-fade' : ''}
                value={feature[0]}
                onDirty={markTextEditDirty}
                onCommit={(nextValue) => applySceneTextChange((prev) => {
                  const nextFeatures = Array.isArray(prev.features) ? [...prev.features] : [...PREVIEW_DEFAULTS.features];
                  const nextFeature = Array.isArray(nextFeatures[index]) ? [...nextFeatures[index]] : ['', ''];
                  nextFeature[0] = nextValue;
                  nextFeatures[index] = nextFeature;
                  return { ...prev, features: nextFeatures };
                })}
              />
              <EditableText
                as="p"
                className={isFading ? 'gen-fade' : ''}
                value={feature[1]}
                onDirty={markTextEditDirty}
                onCommit={(nextValue) => applySceneTextChange((prev) => {
                  const nextFeatures = Array.isArray(prev.features) ? [...prev.features] : [...PREVIEW_DEFAULTS.features];
                  const nextFeature = Array.isArray(nextFeatures[index]) ? [...nextFeatures[index]] : ['', ''];
                  nextFeature[1] = nextValue;
                  nextFeatures[index] = nextFeature;
                  return { ...prev, features: nextFeatures };
                })}
              />
            </div>
          ))}
        </div>
      );
    }

    if (sectionKey === 'stats') {
      return (
        <div className="metric-row" key="stats" data-region="stats" style={getRegionStyle('stats')}>
          {sceneStats.map((stat, index) => (
            <div className="metric" key={stat[1]}>
              <EditableText
                as="div"
                className="metric-value"
                value={stat[0]}
                onDirty={markTextEditDirty}
                onCommit={(nextValue) => applySceneTextChange((prev) => {
                  const nextStats = Array.isArray(prev.stats) ? [...prev.stats] : [...PREVIEW_DEFAULTS.stats];
                  const pair = Array.isArray(nextStats[index]) ? [...nextStats[index]] : ['', ''];
                  pair[0] = nextValue;
                  nextStats[index] = pair;
                  return { ...prev, stats: nextStats };
                })}
              />
              <EditableText
                as="div"
                className="metric-label"
                value={stat[1]}
                onDirty={markTextEditDirty}
                onCommit={(nextValue) => applySceneTextChange((prev) => {
                  const nextStats = Array.isArray(prev.stats) ? [...prev.stats] : [...PREVIEW_DEFAULTS.stats];
                  const pair = Array.isArray(nextStats[index]) ? [...nextStats[index]] : ['', ''];
                  pair[1] = nextValue;
                  nextStats[index] = pair;
                  return { ...prev, stats: nextStats };
                })}
              />
            </div>
          ))}
        </div>
      );
    }

    if (sectionKey === 'testimonials') {
      return (
        <div className="testimonial-row" key="testimonials" data-region="testimonials" style={getRegionStyle('testimonials')}>
          {sceneTestimonials.map((item, index) => (
            <article className="testimonial" key={item.author}>
              <EditableText
                as="p"
                className={isFading ? 'gen-fade' : ''}
                value={item.quote}
                onDirty={markTextEditDirty}
                onCommit={(nextValue) => applySceneTextChange((prev) => {
                  const nextTestimonials = Array.isArray(prev.testimonials) ? [...prev.testimonials] : [...PREVIEW_DEFAULTS.testimonials];
                  nextTestimonials[index] = { ...nextTestimonials[index], quote: nextValue };
                  return { ...prev, testimonials: nextTestimonials };
                })}
              />
              <div className="testimonial-meta">
                <EditableText
                  as="strong"
                  value={item.author}
                  onDirty={markTextEditDirty}
                  onCommit={(nextValue) => applySceneTextChange((prev) => {
                    const nextTestimonials = Array.isArray(prev.testimonials) ? [...prev.testimonials] : [...PREVIEW_DEFAULTS.testimonials];
                    nextTestimonials[index] = { ...nextTestimonials[index], author: nextValue };
                    return { ...prev, testimonials: nextTestimonials };
                  })}
                />
                <EditableText
                  as="span"
                  value={item.role}
                  onDirty={markTextEditDirty}
                  onCommit={(nextValue) => applySceneTextChange((prev) => {
                    const nextTestimonials = Array.isArray(prev.testimonials) ? [...prev.testimonials] : [...PREVIEW_DEFAULTS.testimonials];
                    nextTestimonials[index] = { ...nextTestimonials[index], role: nextValue };
                    return { ...prev, testimonials: nextTestimonials };
                  })}
                />
              </div>
            </article>
          ))}
        </div>
      );
    }

    if (sectionKey === 'faq') {
      return (
        <div className="faq-row" key="faq" data-region="faq" style={getRegionStyle('faq')}>
          {sceneFaq.map((item, index) => (
            <div className="faq-item" key={item.q}>
              <EditableText
                as="h6"
                className={isFading ? 'gen-fade' : ''}
                value={item.q}
                onDirty={markTextEditDirty}
                onCommit={(nextValue) => applySceneTextChange((prev) => {
                  const nextFaq = Array.isArray(prev.faq) ? [...prev.faq] : [...PREVIEW_DEFAULTS.faq];
                  nextFaq[index] = { ...nextFaq[index], q: nextValue };
                  return { ...prev, faq: nextFaq };
                })}
              />
              <EditableText
                as="p"
                className={isFading ? 'gen-fade' : ''}
                value={item.a}
                onDirty={markTextEditDirty}
                onCommit={(nextValue) => applySceneTextChange((prev) => {
                  const nextFaq = Array.isArray(prev.faq) ? [...prev.faq] : [...PREVIEW_DEFAULTS.faq];
                  nextFaq[index] = { ...nextFaq[index], a: nextValue };
                  return { ...prev, faq: nextFaq };
                })}
              />
            </div>
          ))}
        </div>
      );
    }

    return null;
  };

  return (
    <div className="home-redesign">
      <section className="hero">
        <div className="home-wrap">
          <h1 className="headline">Describe your site. <span className="hl bracket">Generate, ReMix, and publish faster.</span></h1>
          <p className="sub">WebBot asks quick choices, your homepage generates in seconds, and you can click any text or image to edit live before publishing.</p>

          <div className="steps" aria-label="three step process">
            <div className="step"><span className="step-num mono">01</span><span className="step-label">Describe it</span></div>
            <div className="step-connector" />
            <div className="step"><span className="step-num mono">02</span><span className="step-label">Watch it generate</span></div>
            <div className="step-connector" />
            <div className="step"><span className="step-num mono">03</span><span className="step-label">Click and publish</span></div>
          </div>

          <form className="prompt-form" onSubmit={onPromptSubmit}>
            <div className="prompt-box">
              <textarea
                id="promptInput"
                ref={promptInputRef}
                className="prompt-input"
                rows="2"
                value={promptValue}
                onChange={(event) => setPromptValue(event.target.value)}
                onKeyDown={onPromptInputKeyDown}
                placeholder={promptPlaceholder}
              />
              <button type="submit" className={`prompt-generate ${isGenerating ? 'loading' : ''}`} disabled={isGenerating}>
                <span className="gen-label">{isGenerating ? 'Building...' : 'Generate my site'}</span>
                <svg className="icon gen-spark" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 3v4M12 17v4M3 12h4M17 12h4M5.6 5.6l2.8 2.8M15.6 15.6l2.8 2.8M5.6 18.4l2.8-2.8M15.6 8.4l2.8-2.8" /></svg>
              </button>
            </div>
            <div className="prompt-chips">
              <button type="button" className="chip" onClick={() => { const text = 'A portfolio site for a wedding photographer, soft and elegant'; setPromptValue(text); setGuidedChoices([]); runGenerate(false, buildGuidedGenerationPrompt(text, [], guidedPersonality), { guidedBasePrompt: text, guidedAnswers: [] }); }}>Photographer portfolio</button>
              <button type="button" className="chip" onClick={() => { const text = 'An online store selling handmade candles, warm and minimal'; setPromptValue(text); setGuidedChoices([]); runGenerate(false, buildGuidedGenerationPrompt(text, [], guidedPersonality), { guidedBasePrompt: text, guidedAnswers: [] }); }}>Candle shop</button>
              <button type="button" className="chip" onClick={() => { const text = 'A landing page for my personal training business, bold and energetic'; setPromptValue(text); setGuidedChoices([]); runGenerate(false, buildGuidedGenerationPrompt(text, [], guidedPersonality), { guidedBasePrompt: text, guidedAnswers: [] }); }}>Personal trainer</button>
              <button type="button" className="chip" onClick={() => { const text = 'A menu and reservations site for a neighborhood coffee shop'; setPromptValue(text); setGuidedChoices([]); runGenerate(false, buildGuidedGenerationPrompt(text, [], guidedPersonality), { guidedBasePrompt: text, guidedAnswers: [] }); }}>Coffee shop</button>
            </div>
          </form>

          <div className="region-question-panel" aria-live="polite">
            <div className="magic-head">
              <span className="magic-label mono">WebBot</span>
            </div>
            {hasGenerated ? (
              <div className="magic-body">
                {isGuidedQuestionVisible ? (
                  <>
                    <p className="magic-question">
                      {isGuidedLoading ? 'Reading the crystal ball...' : (typedGuidedQuestion || guidedQuestion)}
                      <span className="magic-cursor">|</span>
                    </p>
                    <div className="option-row">
                      {(guidedOptions.length === 4 ? guidedOptions : buildGuidedQuestionFallback(guidedChoices).options).map((option) => (
                        <button
                          key={option}
                          type="button"
                          className="option-btn"
                          disabled={isGuidedLoading || isGenerating}
                          onClick={() => onGuidedOptionClick(option)}
                        >
                          {option}
                        </button>
                      ))}
                    </div>
                  </>
                ) : (
                  <p className="magic-waiting">Updating your site...</p>
                )}
              </div>
            ) : <p className="magic-waiting">Generate your site first, then choose from 4 options.</p>}
          </div>

          <div className="avail-hint"><span className="avail-dot" /> {statusText}</div>
          {hasGenerated ? (
            <div className={`gen-source ${generationMeta.usedFallback ? 'warn' : 'ok'}`}>
              Source: {generationMeta.usedFallback ? 'Local fallback' : 'Claude API'}
              {generationMeta.modelUsed ? ` (${generationMeta.modelUsed})` : ''}
              {generationMeta.forcedVariant ? ' - alternate concept mode' : ''}
            </div>
          ) : null}

          <div className={`mockup-frame home-editor-preview ${isPreviewFullscreen ? 'is-fullscreen' : ''}`} ref={canvasRef}>
            <div className="toolbar">
              <div className="tb-group">
                <button type="button" className="tb-btn" onClick={onUndoStyleChange} disabled={!canUndo} aria-label="Undo style change" title="Undo style change"><svg className="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 14L4 9l5-5" /><path d="M4 9h10a5 5 0 0 1 0 10h-1" /></svg></button>
                <button type="button" className="tb-btn" onClick={onRedoStyleChange} disabled={!canRedo} aria-label="Redo style change" title="Redo style change"><svg className="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M15 4l5 5-5 5" /><path d="M20 9H10a5 5 0 0 0 0 10h1" /></svg></button>
                <div className="tb-sep" />
                <button type="button" className={`tb-btn ${previewDevice === 'desktop' ? 'active' : ''}`} onClick={() => setPreviewDevice('desktop')} aria-label="Desktop preview" title="Desktop preview"><svg className="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="5" width="18" height="13" rx="1.5" /><path d="M8 21h8" /></svg></button>
                <button type="button" className={`tb-btn ${previewDevice === 'tablet' ? 'active' : ''}`} onClick={() => setPreviewDevice('tablet')} aria-label="Tablet preview" title="Tablet preview"><svg className="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="5" y="3" width="14" height="18" rx="2" /></svg></button>
                <button type="button" className={`tb-btn ${previewDevice === 'mobile' ? 'active' : ''}`} onClick={() => setPreviewDevice('mobile')} aria-label="Mobile preview" title="Mobile preview"><svg className="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="7" y="2" width="10" height="20" rx="2" /><path d="M11 19h2" /></svg></button>
              </div>
              <div className="tb-group">
                <button
                  type="button"
                  className={`save-btn mono ${isSaveDirty ? 'dirty' : 'clean'}`}
                  disabled={!hasGenerated}
                  onClick={onSaveEditorChanges}
                >
                  Save
                </button>
                <span className="save-meta mono">{saveMetaLabel}</span>
                <div className="tb-sep" />
                <button type="button" className={`regen-btn mono ${isRegenerating ? 'spinning' : ''}`} onClick={onRegenerate}>
                  <svg className="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 12a9 9 0 0 1 15-6.7L21 8M21 3v5h-5" /><path d="M21 12a9 9 0 0 1-15 6.7L3 16M3 21v-5h5" /></svg>
                  ReMix
                </button>
                <div className="tb-sep" />
                <button type="button" className="zoom mono" onClick={cycleZoom} title="Cycle zoom">{zoomLevel}% <span>v</span></button>
                <div className="tb-sep" />
                <button type="button" className={`tb-btn ${isPreviewFullscreen ? 'active' : ''}`} onClick={() => setIsPreviewFullscreen(prev => !prev)} aria-label="Toggle fullscreen preview" title="Toggle fullscreen preview"><svg className="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" /></svg></button>
              </div>
            </div>

            <div className={`body-row device-${previewDevice}`}>
              <div className={`preview-stage device-${previewDevice}`}>
                <div className={`canvas ${canvasVariantClass}`} style={canvasStyle} onMouseMove={onCanvasMouseMove} onMouseLeave={onCanvasMouseLeave} data-region="global">
                <div className={`skeleton-overlay ${isGenerating ? 'active' : ''}`}>
                  <div className="sk-row" style={{ width: '40%' }} />
                  <div className="sk-block" />
                  <div className="sk-row" style={{ width: '85%' }} />
                  <div className="sk-row" style={{ width: '60%' }} />
                  <div className="sk-pill" />
                  <div className="sk-grid">
                    <div className="sk-card" />
                    <div className="sk-card" />
                    <div className="sk-card" />
                  </div>
                </div>

                {sceneDesignPreset === 'editorial' ? (
                  <div className={`editorial-canvas ${isFading ? 'gen-fade' : ''}`}>
                    <div className="editorial-topbar" data-region="nav" style={getRegionStyle('nav')}>
                      <div className="editorial-brand-wrap">
                        {customLogoDataUrl ? (
                          <img src={customLogoDataUrl} alt="Uploaded logo" className="canvas-uploaded-logo" />
                        ) : null}
                        <EditableText
                          as="div"
                          className="editorial-brand"
                          value={scene.brand}
                          onDirty={markTextEditDirty}
                          onCommit={(nextValue) => applySceneTextChange((prev) => ({ ...prev, brand: nextValue }))}
                        />
                      </div>
                      <div className="editorial-nav">
                        {sceneNavItems.map((item, index) => (
                          <EditableText
                            as="span"
                            key={`${item}-${index}`}
                            value={item}
                            onDirty={markTextEditDirty}
                            onCommit={(nextValue) => applySceneTextChange((prev) => {
                              const nextItems = Array.isArray(prev.navItems) ? [...prev.navItems] : [...PREVIEW_DEFAULTS.navItems];
                              nextItems[index] = nextValue;
                              return { ...prev, navItems: nextItems };
                            })}
                          />
                        ))}
                      </div>
                    </div>

                    <div className="editorial-hero" data-region="editorialHero" style={getRegionStyle('editorialHero')}>
                      <EditableText
                        as="div"
                        className="editorial-tag mono"
                        value={scene.tag}
                        onDirty={markTextEditDirty}
                        onCommit={(nextValue) => applySceneTextChange((prev) => ({ ...prev, tag: nextValue }))}
                      />
                      <h3>
                        <EditableText
                          value={scene.headlineParts[0]}
                          onDirty={markTextEditDirty}
                          onCommit={(nextValue) => applySceneTextChange((prev) => ({
                            ...prev,
                            headlineParts: [nextValue, prev.headlineParts[1]],
                          }))}
                        />
                        <EditableText
                          as="span"
                          value={scene.headlineParts[1]}
                          onDirty={markTextEditDirty}
                          onCommit={(nextValue) => applySceneTextChange((prev) => ({
                            ...prev,
                            headlineParts: [prev.headlineParts[0], nextValue],
                          }))}
                        />
                      </h3>
                      <EditableText
                        as="p"
                        value={scene.sub}
                        onDirty={markTextEditDirty}
                        onCommit={(nextValue) => applySceneTextChange((prev) => ({ ...prev, sub: nextValue }))}
                      />
                      <EditableText
                        as="span"
                        className="editorial-cta"
                        value={scene.cta}
                        onDirty={markTextEditDirty}
                        onCommit={(nextValue) => applySceneTextChange((prev) => ({ ...prev, cta: nextValue }))}
                      />
                    </div>

                    <svg className="editorial-thread" viewBox="0 0 520 34" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                      <path d="M2 17 C 90 2, 150 31, 240 17 S 400 2, 518 17" />
                    </svg>

                    <div className="editorial-studio" data-region="editorialHero" style={getRegionStyle('editorialHero')}>
                      <div
                        className={`studio-media image-click-target ${selectedImageTarget && selectedImageTarget.target === 'studio' ? 'active-image-target' : ''}`}
                        style={{ ...getRegionStyle('editorialHero'), backgroundImage: `url(${resolvedStudioImage})` }}
                        onClick={() => onImageTargetClick('studio')}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            onImageTargetClick('studio');
                          }
                        }}
                      >
                        <EditableText
                          as="span"
                          value={scene.media}
                          onDirty={markTextEditDirty}
                          onCommit={(nextValue) => applySceneTextChange((prev) => ({ ...prev, media: nextValue }))}
                        />
                      </div>
                      <div className="studio-copy">
                        <div className="studio-eyebrow">The Story</div>
                        <EditableText
                          as="h4"
                          value={sceneTestimonials[0].author}
                          onDirty={markTextEditDirty}
                          onCommit={(nextValue) => applySceneTextChange((prev) => {
                            const nextTestimonials = Array.isArray(prev.testimonials) ? [...prev.testimonials] : [...PREVIEW_DEFAULTS.testimonials];
                            nextTestimonials[0] = { ...nextTestimonials[0], author: nextValue };
                            return { ...prev, testimonials: nextTestimonials };
                          })}
                        />
                        <EditableText
                          as="p"
                          value={sceneTestimonials[0].quote}
                          onDirty={markTextEditDirty}
                          onCommit={(nextValue) => applySceneTextChange((prev) => {
                            const nextTestimonials = Array.isArray(prev.testimonials) ? [...prev.testimonials] : [...PREVIEW_DEFAULTS.testimonials];
                            nextTestimonials[0] = { ...nextTestimonials[0], quote: nextValue };
                            return { ...prev, testimonials: nextTestimonials };
                          })}
                        />
                        <div className="studio-stats-mini">
                          {sceneStats.map((stat, index) => (
                            <div key={stat[1]}>
                              <EditableText
                                as="strong"
                                value={stat[0]}
                                onDirty={markTextEditDirty}
                                onCommit={(nextValue) => applySceneTextChange((prev) => {
                                  const nextStats = Array.isArray(prev.stats) ? [...prev.stats] : [...PREVIEW_DEFAULTS.stats];
                                  const pair = Array.isArray(nextStats[index]) ? [...nextStats[index]] : ['', ''];
                                  pair[0] = nextValue;
                                  nextStats[index] = pair;
                                  return { ...prev, stats: nextStats };
                                })}
                              />
                              <EditableText
                                as="span"
                                value={stat[1]}
                                onDirty={markTextEditDirty}
                                onCommit={(nextValue) => applySceneTextChange((prev) => {
                                  const nextStats = Array.isArray(prev.stats) ? [...prev.stats] : [...PREVIEW_DEFAULTS.stats];
                                  const pair = Array.isArray(nextStats[index]) ? [...nextStats[index]] : ['', ''];
                                  pair[1] = nextValue;
                                  nextStats[index] = pair;
                                  return { ...prev, stats: nextStats };
                                })}
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div className="editorial-gallery" data-region="editorialGallery" style={getRegionStyle('editorialGallery')}>
                      {editorialGalleryLabels.map((label, index) => (
                        <div
                          className={`gallery-tile tile-${index + 1} image-click-target ${selectedImageTarget && selectedImageTarget.target === 'gallery' && selectedImageTarget.index === index ? 'active-image-target' : ''}`}
                          key={label}
                          style={{ backgroundImage: `url(${resolvedGalleryImages[index] || resolvedHeroImage})` }}
                          onClick={() => onImageTargetClick('gallery', index)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              onImageTargetClick('gallery', index);
                            }
                          }}
                        >
                          {label}
                        </div>
                      ))}
                    </div>

                    <div className="editorial-services" data-region="editorialServices" style={getRegionStyle('editorialServices')}>
                      {editorialServiceItems.map((item, index) => (
                        <div className="service-tile" key={`${item[0]}-${index}`}>
                          <span>{`${index + 1}`.padStart(2, '0')}</span>
                          <EditableText
                            as="h5"
                            value={item[0]}
                            onDirty={markTextEditDirty}
                            onCommit={(nextValue) => applySceneTextChange((prev) => {
                              if (index < 3) {
                                const nextFeatures = Array.isArray(prev.features) ? [...prev.features] : [...PREVIEW_DEFAULTS.features];
                                const feature = Array.isArray(nextFeatures[index]) ? [...nextFeatures[index]] : ['', ''];
                                feature[0] = nextValue;
                                nextFeatures[index] = feature;
                                return { ...prev, features: nextFeatures };
                              }

                              const faqIndex = index - 3;
                              const nextFaq = Array.isArray(prev.faq) ? [...prev.faq] : [...PREVIEW_DEFAULTS.faq];
                              nextFaq[faqIndex] = { ...nextFaq[faqIndex], q: nextValue };
                              return { ...prev, faq: nextFaq };
                            })}
                          />
                          <EditableText
                            as="p"
                            value={item[1]}
                            onDirty={markTextEditDirty}
                            onCommit={(nextValue) => applySceneTextChange((prev) => {
                              if (index < 3) {
                                const nextFeatures = Array.isArray(prev.features) ? [...prev.features] : [...PREVIEW_DEFAULTS.features];
                                const feature = Array.isArray(nextFeatures[index]) ? [...nextFeatures[index]] : ['', ''];
                                feature[1] = nextValue;
                                nextFeatures[index] = feature;
                                return { ...prev, features: nextFeatures };
                              }

                              const faqIndex = index - 3;
                              const nextFaq = Array.isArray(prev.faq) ? [...prev.faq] : [...PREVIEW_DEFAULTS.faq];
                              nextFaq[faqIndex] = { ...nextFaq[faqIndex], a: nextValue };
                              return { ...prev, faq: nextFaq };
                            })}
                          />
                        </div>
                      ))}
                    </div>

                    <div className="editorial-quote" data-region="editorialQuote" style={getRegionStyle('editorialQuote')}>
                      <EditableText
                        as="p"
                        value={sceneTestimonials[1].quote}
                        onDirty={markTextEditDirty}
                        onCommit={(nextValue) => applySceneTextChange((prev) => {
                          const nextTestimonials = Array.isArray(prev.testimonials) ? [...prev.testimonials] : [...PREVIEW_DEFAULTS.testimonials];
                          nextTestimonials[1] = { ...nextTestimonials[1], quote: nextValue };
                          return { ...prev, testimonials: nextTestimonials };
                        })}
                      />
                      <cite>
                        <EditableText
                          value={sceneTestimonials[1].author}
                          onDirty={markTextEditDirty}
                          onCommit={(nextValue) => applySceneTextChange((prev) => {
                            const nextTestimonials = Array.isArray(prev.testimonials) ? [...prev.testimonials] : [...PREVIEW_DEFAULTS.testimonials];
                            nextTestimonials[1] = { ...nextTestimonials[1], author: nextValue };
                            return { ...prev, testimonials: nextTestimonials };
                          })}
                        />
                        {' - '}
                        <EditableText
                          value={sceneTestimonials[1].role}
                          onDirty={markTextEditDirty}
                          onCommit={(nextValue) => applySceneTextChange((prev) => {
                            const nextTestimonials = Array.isArray(prev.testimonials) ? [...prev.testimonials] : [...PREVIEW_DEFAULTS.testimonials];
                            nextTestimonials[1] = { ...nextTestimonials[1], role: nextValue };
                            return { ...prev, testimonials: nextTestimonials };
                          })}
                        />
                      </cite>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="canvas-topbar" data-region="nav" style={getRegionStyle('nav')}>
                      <div className="canvas-logo">
                        {customLogoDataUrl ? (
                          <img src={customLogoDataUrl} alt="Uploaded logo" className="canvas-uploaded-logo" />
                        ) : <span className="dot" style={{ background: scene.accent }} />}
                        <EditableText value={scene.brand} onDirty={markTextEditDirty} onCommit={(nextValue) => applySceneTextChange((prev) => ({ ...prev, brand: nextValue }))} />
                      </div>
                      <div className="canvas-menu">
                        {sceneNavItems.map((item, index) => (
                          <EditableText
                            as="span"
                            key={`${item}-${index}`}
                            value={item}
                            onDirty={markTextEditDirty}
                            onCommit={(nextValue) => applySceneTextChange((prev) => {
                              const nextItems = Array.isArray(prev.navItems) ? [...prev.navItems] : [...PREVIEW_DEFAULTS.navItems];
                              nextItems[index] = nextValue;
                              return { ...prev, navItems: nextItems };
                            })}
                          />
                        ))}
                      </div>
                      <EditableText
                        as="div"
                        className="canvas-btn-ghost"
                        value={scene.ctaTop}
                        onDirty={markTextEditDirty}
                        onCommit={(nextValue) => applySceneTextChange((prev) => ({ ...prev, ctaTop: nextValue }))}
                      />
                    </div>

                    <div className="canvas-hero" data-region="hero" style={getRegionStyle('hero')}>
                      <div>
                        <EditableText
                          as="div"
                          className={`tag mono ${isFading ? 'gen-fade' : ''}`}
                          value={scene.tag}
                          onDirty={markTextEditDirty}
                          onCommit={(nextValue) => applySceneTextChange((prev) => ({ ...prev, tag: nextValue }))}
                        />
                        <h3 className={isFading ? 'gen-fade' : ''}>
                          <EditableText
                            value={scene.headlineParts[0]}
                            onDirty={markTextEditDirty}
                            onCommit={(nextValue) => applySceneTextChange((prev) => ({
                              ...prev,
                              headlineParts: [nextValue, prev.headlineParts[1]],
                            }))}
                          />
                          <EditableText
                            as="span"
                            className="selected bracket bracket--teal"
                            value={scene.headlineParts[1]}
                            onDirty={markTextEditDirty}
                            onCommit={(nextValue) => applySceneTextChange((prev) => ({
                              ...prev,
                              headlineParts: [prev.headlineParts[0], nextValue],
                            }))}
                          />
                        </h3>
                        <EditableText
                          as="p"
                          className={isFading ? 'gen-fade' : ''}
                          value={scene.sub}
                          onDirty={markTextEditDirty}
                          onCommit={(nextValue) => applySceneTextChange((prev) => ({ ...prev, sub: nextValue }))}
                        />
                        <EditableText
                          as="span"
                          className={`cta-mini ${isFading ? 'gen-fade' : ''}`}
                          value={scene.cta}
                          onDirty={markTextEditDirty}
                          onCommit={(nextValue) => applySceneTextChange((prev) => ({ ...prev, cta: nextValue }))}
                        />
                      </div>
                      <div
                        className={`hero-media image-click-target ${isFading ? 'gen-fade' : ''} ${selectedImageTarget && selectedImageTarget.target === 'hero' ? 'active-image-target' : ''}`}
                        style={{ ...getRegionStyle('hero'), backgroundImage: `url(${resolvedHeroImage})` }}
                        onClick={() => onImageTargetClick('hero')}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            onImageTargetClick('hero');
                          }
                        }}
                      >
                        <EditableText
                          as="span"
                          value={scene.media}
                          onDirty={markTextEditDirty}
                          onCommit={(nextValue) => applySceneTextChange((prev) => ({ ...prev, media: nextValue }))}
                        />
                      </div>
                    </div>

                    {sceneSectionOrder.map((sectionKey) => renderGeneratedSection(sectionKey))}
                  </>
                )}
                </div>
              </div>

              <div className="panel">
                <div className="panel-head">
                  <span className="panel-title">Style</span>
                  <span className="panel-caret">^</span>
                </div>

                <div className="panel-section">
                  <div className="panel-label"><span>Font type</span></div>
                  <div className="font-row">
                    <select
                      className="font-select"
                      value={typeof globalChoices.fontFamily === 'string' ? globalChoices.fontFamily : ''}
                      disabled={!hasGenerated}
                      onChange={(event) => applyGlobalStyleValue('fontFamily', event.target.value)}
                    >
                      {FONT_FAMILY_OPTIONS.map((option) => (
                        <option key={option.key || 'theme-default'} value={option.key}>{option.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="panel-section">
                  <div className="panel-label"><span>Background color</span></div>
                  <div className="picker-row">
                    <input
                      type="color"
                      className="picker-input"
                      value={panelBackgroundColor}
                      disabled={!hasGenerated}
                      onChange={(event) => applyGlobalStyleValue('customBackground', event.target.value)}
                    />
                    <span className="mono picker-value">{panelBackgroundColor}</span>
                  </div>
                </div>

                <div className="panel-section">
                  <div className="panel-label"><span>Logo</span></div>
                  <div className="logo-upload-row">
                    <label className={`logo-upload-btn ${!hasGenerated ? 'disabled' : ''}`} aria-label="Upload logo">
                      Upload logo
                      <input
                        type="file"
                        accept="image/*"
                        disabled={!hasGenerated}
                        onChange={onLogoFileChange}
                      />
                    </label>
                    <button
                      type="button"
                      className="logo-clear-btn"
                      onClick={onClearUploadedLogo}
                      disabled={!hasGenerated || !customLogoDataUrl}
                    >
                      Remove
                    </button>
                  </div>
                  <p className="logo-upload-hint">PNG, JPG, SVG accepted.</p>
                </div>

                <div className="panel-section">
                  <div className="panel-label"><span>Font color</span></div>
                  <div className="picker-row">
                    <input
                      type="color"
                      className="picker-input"
                      value={panelFontColor}
                      disabled={!hasGenerated}
                      onChange={(event) => applyGlobalStyleValue('customFontColor', event.target.value)}
                    />
                    <span className="mono picker-value">{panelFontColor}</span>
                  </div>
                  {hasGenerated && !selectedImageTarget ? (
                    <p className="gallery-inline-hint">Click an image in the preview to open the image gallery picker.</p>
                  ) : null}
                </div>

                {hasGenerated && selectedImageTarget ? (
                  <div className="panel-section">
                    <div className="panel-label"><span>Image gallery</span></div>
                    <p className="gallery-help">
                      {`Selected: ${IMAGE_TARGET_LABELS[selectedImageTarget.target] || 'Image'}${selectedImageTarget.target === 'gallery' && Number.isInteger(selectedImageTarget.index) ? ` ${selectedImageTarget.index + 1}` : ''}`}
                    </p>
                    <div className="gallery-grid">
                      {galleryPickerOptions.map((imageUrl, index) => (
                        <button
                          key={`${imageUrl}-${index}`}
                          type="button"
                          className="gallery-thumb"
                          disabled={!hasGenerated}
                          onClick={() => onGalleryImagePick(imageUrl)}
                          aria-label={`Use gallery image ${index + 1}`}
                        >
                          <img
                            src={imageUrl}
                            alt={`Gallery option ${index + 1}`}
                            loading="lazy"
                            onError={(event) => {
                              event.currentTarget.onerror = null;
                              event.currentTarget.src = resolvedHeroImage || imageSet.hero;
                            }}
                          />
                        </button>
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          </div>

          <div className="mockup-caption">Selected: <span className="mono">h1.headline</span> - click any element on the canvas to edit it here</div>
          {hasGenerated && lastPromptUsed ? (
            <div className="mockup-prompt mono">Prompt used: {lastPromptUsed}</div>
          ) : null}

          <div className="publish-bar">
            <span className="publish-copy">
              {hasGenerated
                ? 'Like it? Create a free account to keep this draft, then grab a domain and publish.'
                : 'Like it? Grab a domain and publish - free to start.'}
            </span>
            {hasGenerated ? (
              <button
                type="button"
                className="publish-signup-btn"
                onClick={onContinueToSignup}
                disabled={SIGNUP_DISABLED}
                title={SIGNUP_DISABLED ? 'Signup is temporarily disabled.' : undefined}
              >
                Keep this draft
              </button>
            ) : null}
            <form className="publish-form" onSubmit={onPublishSubmit}>
              <input
                type="text"
                placeholder="yourbrand.com"
                autoComplete="off"
                onKeyDown={handleKeyDown}
                onChange={handleOnChange}
                value={domainName.name}
                aria-label="Domain Search"
              />
              <button type="submit">Claim and publish</button>
            </form>
          </div>
        </div>
      </section>

      <section className="home-positioning">
        <div className="home-wrap">
          <div className="section-head">
            <div className="eyebrow center">Why editpage</div>
            <h2>Every other builder makes you choose: talk to a chatbot, or wrestle a drag-and-drop canvas.</h2>
          </div>
          <div className="compare-grid">
            <div className="compare-col">
              <div className="compare-label mono">Legacy builders</div>
              <div className="compare-item"><span className="mark mark-negative"><svg className="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="M18 6L6 18M6 6l12 12" /></svg></span> Pick from a grid of templates that all look the same</div>
              <div className="compare-item"><span className="mark mark-negative"><svg className="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="M18 6L6 18M6 6l12 12" /></svg></span> Drag elements onto a grid and fight the snapping</div>
              <div className="compare-item"><span className="mark mark-negative"><svg className="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="M18 6L6 18M6 6l12 12" /></svg></span> Monthly bill starts the day you sign up, earning or not</div>
              <div className="compare-item"><span className="mark mark-negative"><svg className="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="M18 6L6 18M6 6l12 12" /></svg></span> Plans that hide storage and bandwidth behind add-ons</div>
            </div>
            <div className="compare-col compare-col-featured">
              <div className="compare-label mono">EditPage</div>
              <div className="compare-item"><span className="mark mark-positive"><svg className="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="M20 7L10 17l-6-6" /></svg></span> Describe your site and watch it generate before your eyes</div>
              <div className="compare-item"><span className="mark mark-positive"><svg className="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="M20 7L10 17l-6-6" /></svg></span> No canvas, no snapping, click the exact thing you want to change</div>
              <div className="compare-item"><span className="mark mark-positive"><svg className="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="M20 7L10 17l-6-6" /></svg></span> Start free and upgrade to Growth, Scale, or Enterprise when ready</div>
              <div className="compare-item"><span className="mark mark-positive"><svg className="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="M20 7L10 17l-6-6" /></svg></span> Unlimited bandwidth included, no add-ons required</div>
            </div>
          </div>
        </div>
      </section>

      <section className="home-features">
        <div className="home-wrap">
          <div className="section-head">
            <div className="eyebrow center">Built for speed</div>
            <h2>Everything you need to launch, nothing you have to learn</h2>
          </div>
          <div className="feat-grid">
            {FEATURE_CARDS.map((card, index) => (
              <article className={`feat-card ${index === 0 ? 'bracket' : ''}`} key={card.title}>
                <div className="feat-icon mono">{`0${index + 1}`.slice(-2)}</div>
                <h4>{card.title}</h4>
                <p>{card.description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="home-pricing">
        <div className="home-wrap">
          <div className="section-head">
            <div className="eyebrow center">Pricing that grows with you</div>
            <h2>Simple pricing built to stay competitive as you grow</h2>
            <p className="sub">Start free, upgrade when you need more power, and keep predictable pricing at every stage.</p>
            <p className="sub annual-callout">Annual plans save up to 20% on Growth and Scale.</p>
          </div>
          <div className="cards">
            {PRICING_CARDS.map(card => (
              <article className={`card ${card.featured ? 'featured bracket' : ''}`} key={card.name}>
                {card.badge ? <div className="badge">{card.badge}</div> : null}
                <h4>{card.name}</h4>
                <div className="desc">{card.description}</div>
                <div className="price">{card.price}<span>{card.priceSuffix}</span></div>
                {card.socialProof ? <div className="social-proof">{card.socialProof}</div> : null}
                {card.annualNote ? <div className="annual-note">{card.annualNote}</div> : null}
                <ul className="feat-list">
                  {card.features.map(item => (
                    <li key={item}><span className="check">+</span> {item}</li>
                  ))}
                </ul>
                <button
                  type="button"
                  className={`card-cta ${card.featured ? 'solid' : 'outline'}`}
                  disabled={SIGNUP_DISABLED && isSignupHref(card.ctaHref)}
                  title={SIGNUP_DISABLED && isSignupHref(card.ctaHref) ? 'Signup is temporarily disabled.' : undefined}
                  onClick={() => onPricingCtaClick(card.ctaHref)}
                >
                  {card.cta}
                </button>
              </article>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomepageContent;
